import { useState, useEffect, useMemo, useRef } from 'react';
import { ClientEvent, SyncState, UserEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';

/**
 * Returns the display name for a User or RoomMember object.
 * User objects have rawDisplayName/displayName; RoomMember objects have rawDisplayName/name.
 */
const getName = (u) => u.rawDisplayName || u.displayName || u.name || '';

/**
 * Lightweight hook that collects {id, name} pairs for all known users,
 * including the current user. Subscribes only to Sync and User events —
 * no avatar fetching or extra state.
 *
 * @returns {{ namesList: Array<{id: string, name: string}> }}
 */
export const useContactNames = () => {
  const client = useMatrixClient();
  const [users, setUsers] = useState([]);
  const fingerprintRef = useRef('');

  useEffect(() => {
    if (!client) {
      setUsers([]);
      return;
    }

    const updateUsers = () => {
      // Get all cached users from the SDK
      const allUsers = client.getUsers();

      // Also get all users from room memberships to include invited users
      // who may not be in the SDK's user cache yet.
      // We use RoomMember objects directly as fallback because client.getUser()
      // returns null for users who are only invited (not joined) to rooms.
      const rooms = client.getRooms?.() || [];
      const existingUserIds = new Set(allUsers.map(u => u.userId));
      const additionalUsers = [];

      rooms.forEach(room => {
        const members = room.getMembers?.() || [];
        members.forEach(member => {
          if (!existingUserIds.has(member.userId)) {
            existingUserIds.add(member.userId);
            // Try SDK user cache first, fall back to RoomMember object directly.
            // RoomMember objects have userId/rawDisplayName/name properties
            // needed by the downstream namesKey and namesList computations.
            const user = client.getUser?.(member.userId);
            additionalUsers.push(user || member);
          }
        });
      });

      // Combine SDK users with room member users (includes current user)
      const combined = [...allUsers, ...additionalUsers];

      // Value-based change detection: compute a fingerprint of userId+displayName
      // and skip the state update if nothing actually changed. This avoids triggering
      // the O(n log n) namesKey recomputation on every sync tick (every 3-30s).
      const fingerprint = combined
        .map(u => `${u.userId}\0${getName(u)}`)
        .sort()
        .join('\n');

      if (fingerprint !== fingerprintRef.current) {
        fingerprintRef.current = fingerprint;
        setUsers(combined);
      }
    };

    const handleSync = (state) => {
      if (state === SyncState.Prepared || state === SyncState.Syncing) {
        updateUsers();
      }
    };

    // Initial state check
    const syncState = client.getSyncState();
    if (syncState === SyncState.Prepared || syncState === SyncState.Syncing) {
      updateUsers();
    }

    client.on(ClientEvent.Sync, handleSync);
    client.on(UserEvent.DisplayName, updateUsers);

    return () => {
      client.off(ClientEvent.Sync, handleSync);
      client.off(UserEvent.DisplayName, updateUsers);
    };
  }, [client]);

  // Build a stable key that only changes when names or IDs actually change
  const namesKey = useMemo(
    () => users.map(u => `${u.userId}\0${getName(u)}`).sort().join('\n'),
    [users],
  );

  const namesList = useMemo(
    () => users.map(u => ({
      id: u.userId,
      name: getName(u) || u.userId,
    })),
    [namesKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return { namesList };
};
